import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { Phone, MapPin, Wind, Wifi, Tv, Bath, ArrowUpRight, Menu, X } from "lucide-react";
import { translations, media, PHONE, PHONE_TEL } from "./translations";

/* Reveal-on-scroll hook */
function useReveal() {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}

/* === LANGUAGE SWITCHER === */
const LanguageSwitcher = ({ lang, setLang }) => (
  <div
    data-testid="language-switcher"
    className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-medium"
  >
    <button
      data-testid="lang-ro"
      onClick={() => setLang("ro")}
      className={`transition-colors duration-300 ${
        lang === "ro" ? "text-[#2B4A3B]" : "text-black/40 hover:text-black"
      }`}
    >
      RO
    </button>
    <span className="wr-lang-divider">|</span>
    <button
      data-testid="lang-en"
      onClick={() => setLang("en")}
      className={`transition-colors duration-300 ${
        lang === "en" ? "text-[#2B4A3B]" : "text-black/40 hover:text-black"
      }`}
    >
      EN
    </button>
  </div>
);

/* === HEADER === */
const Header = ({ t, lang, setLang }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navItems = [
    { id: "about", label: t.nav.about, testid: "nav-link-about" },
    { id: "amenities", label: t.nav.amenities, testid: "nav-link-amenities" },
    { id: "gallery", label: t.nav.gallery, testid: "nav-link-gallery" },
    { id: "contact", label: t.nav.contact, testid: "nav-link-contact" },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
        scrolled
          ? "bg-[#FBF9F6]/85 backdrop-blur-xl border-b border-[#E5E0D8]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <a
          href="#top"
          data-testid="brand-mark"
          className={`font-serif text-xl md:text-2xl tracking-tight transition-colors ${
            scrolled ? "text-[#1A1A1A]" : "text-white"
          }`}
        >
          White Residence<span className="text-[#C2B280]">.</span>
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {navItems.map((item) => (
            <a
              key={item.id}
              data-testid={item.testid}
              href={`#${item.id}`}
              className={`text-[12px] tracking-[0.18em] uppercase font-medium transition-colors duration-300 ${
                scrolled ? "text-[#1A1A1A] hover:text-[#2B4A3B]" : "text-white/90 hover:text-white"
              }`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-6">
          <div className={scrolled ? "text-[#1A1A1A]" : "text-white"}>
            <LanguageSwitcher lang={lang} setLang={setLang} />
          </div>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className={`md:hidden ${scrolled ? "text-[#1A1A1A]" : "text-white"}`}
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu-panel"
          className="md:hidden bg-[#FBF9F6] border-t border-[#E5E0D8]"
        >
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((item) => (
              <a
                key={item.id}
                data-testid={`mobile-${item.testid}`}
                href={`#${item.id}`}
                onClick={() => setOpen(false)}
                className="text-[13px] tracking-[0.18em] uppercase font-medium text-[#1A1A1A]"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-4 border-t border-[#E5E0D8]">
              <LanguageSwitcher lang={lang} setLang={setLang} />
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

/* === HERO === */
const Hero = ({ t }) => (
  <section
    id="top"
    data-testid="hero-section"
    className="relative min-h-[92vh] w-full overflow-hidden wr-grain"
  >
    <div className="absolute inset-0">
      <img
        src={media.hero}
        alt="White Residence Arad — suite"
        className="w-full h-full object-cover wr-kenburns"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/55" />
    </div>

    <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 min-h-[92vh] flex flex-col justify-end pb-36 md:pb-48">
      <div className="max-w-3xl">
        <div className="reveal">
          <span
            className="inline-block text-[11px] tracking-[0.3em] uppercase font-medium text-white/85 border-l-2 border-[#C2B280] pl-4"
            data-testid="hero-overline"
          >
            {t.hero.overline}
          </span>
        </div>

        <h1
          data-testid="hero-title"
          className="reveal font-serif text-white mt-8 text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.02] font-light"
          style={{ transitionDelay: "120ms" }}
        >
          {t.hero.title}
        </h1>

        <p
          data-testid="hero-subtitle"
          className="reveal mt-6 max-w-xl text-white/85 text-base md:text-lg font-light leading-relaxed"
          style={{ transitionDelay: "220ms" }}
        >
          {t.hero.subtitle}
        </p>

        <div
          className="reveal mt-10 flex flex-wrap items-center gap-4"
          style={{ transitionDelay: "320ms" }}
        >
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="call-to-reserve-hero-button"
            className="group inline-flex items-center gap-3 bg-[#2B4A3B] hover:bg-[#1D3227] text-white px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-medium transition-all duration-300 active:scale-95"
          >
            <Phone size={16} strokeWidth={1.6} />
            <span>{t.hero.cta}</span>
            <ArrowUpRight
              size={16}
              strokeWidth={1.5}
              className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
            />
          </a>

          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white px-5 py-4 text-[12px] tracking-[0.22em] uppercase font-medium border border-white/30 hover:border-white/60 transition-colors duration-300"
            data-testid="hero-secondary-cta"
          >
            <MapPin size={15} strokeWidth={1.5} />
            <span>Voluntarilor 1, Arad</span>
          </a>
        </div>
      </div>
    </div>

    {/* Scroll cue */}
    <div className="absolute bottom-6 right-6 md:right-12 z-10 hidden md:flex flex-col items-center gap-3 text-white/70">
      <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
      <span className="w-px h-12 bg-white/40" />
    </div>
  </section>
);

/* === ABOUT === */
const About = ({ t }) => (
  <section
    id="about"
    data-testid="about-section"
    className="relative bg-[#FBF9F6] py-24 md:py-32 lg:py-40"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
      <div className="lg:col-span-5 reveal wr-zoom rounded-sm">
        <img
          src={media.about_texture}
          alt="Interior texture"
          className="w-full h-[420px] md:h-[520px] object-cover"
        />
      </div>

      <div className="lg:col-span-7 lg:pl-8">
        <span className="wr-overline reveal">— {t.about.label}</span>
        <h2
          data-testid="about-headline"
          className="reveal font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light mt-6 text-[#1A1A1A]"
          style={{ transitionDelay: "100ms" }}
        >
          {t.about.headline}
        </h2>
        <p
          className="reveal mt-8 text-[#4A4A4A] text-base md:text-lg leading-relaxed max-w-2xl font-light"
          style={{ transitionDelay: "180ms" }}
        >
          {t.about.text}
        </p>

        <div
          className="reveal mt-12 grid grid-cols-3 gap-6 md:gap-10 border-t border-[#E5E0D8] pt-10 max-w-2xl"
          style={{ transitionDelay: "260ms" }}
        >
          {t.about.stats.map((s, i) => (
            <div key={i} data-testid={`about-stat-${i}`}>
              <div className="font-serif text-3xl md:text-4xl text-[#2B4A3B] font-light">
                {s.value}
              </div>
              <div className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[#4A4A4A]">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* === AMENITIES === */
const AMENITY_ICONS = [Wind, Wifi, Tv, Bath];

const Amenities = ({ t }) => (
  <section
    id="amenities"
    data-testid="amenities-section"
    className="relative bg-[#F3EFEA] py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="max-w-3xl">
        <span className="wr-overline reveal">— {t.amenities.label}</span>
        <h2
          data-testid="amenities-headline"
          className="reveal font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light mt-6 text-[#1A1A1A]"
          style={{ transitionDelay: "100ms" }}
        >
          {t.amenities.headline}
        </h2>
      </div>

      <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E5E0D8] border border-[#E5E0D8]">
        {t.amenities.items.map((item, i) => {
          const Icon = AMENITY_ICONS[i % AMENITY_ICONS.length];
          return (
            <div
              key={i}
              data-testid={`amenity-card-${i}`}
              className="group bg-[#FBF9F6] p-8 md:p-10 transition-colors duration-500 hover:bg-white reveal"
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              <div className="w-12 h-12 flex items-center justify-center border border-[#E5E0D8] rounded-full text-[#2B4A3B] group-hover:border-[#2B4A3B] transition-colors duration-500">
                <Icon size={20} strokeWidth={1.3} />
              </div>
              <h3 className="mt-8 font-serif text-2xl text-[#1A1A1A] leading-snug">
                {item.title}
              </h3>
              <p className="mt-3 text-[#4A4A4A] text-sm leading-relaxed font-light">
                {item.desc}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

/* === GALLERY (Bento) === */
const Gallery = ({ t }) => (
  <section
    id="gallery"
    data-testid="gallery-section"
    className="bg-[#FBF9F6] py-24 md:py-32"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 max-w-5xl">
        <div>
          <span className="wr-overline reveal">— {t.gallery.label}</span>
          <h2
            data-testid="gallery-headline"
            className="reveal font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light mt-6 text-[#1A1A1A]"
            style={{ transitionDelay: "100ms" }}
          >
            {t.gallery.headline}
          </h2>
        </div>
        <p className="reveal text-[#4A4A4A] text-base font-light max-w-md" style={{ transitionDelay: "180ms" }}>
          {t.gallery.sub}
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
        <div
          data-testid="gallery-item-0"
          className="md:col-span-8 wr-zoom rounded-sm reveal"
        >
          <img
            src={media.gallery[0].url}
            alt={media.gallery[0].alt}
            className="w-full h-[360px] md:h-[560px] object-cover"
          />
        </div>
        <div
          data-testid="gallery-item-1"
          className="md:col-span-4 wr-zoom rounded-sm reveal"
          style={{ transitionDelay: "100ms" }}
        >
          <img
            src={media.gallery[1].url}
            alt={media.gallery[1].alt}
            className="w-full h-[360px] md:h-[560px] object-cover"
          />
        </div>
        <div
          data-testid="gallery-item-2"
          className="md:col-span-5 wr-zoom rounded-sm reveal"
          style={{ transitionDelay: "60ms" }}
        >
          <img
            src={media.gallery[2].url}
            alt={media.gallery[2].alt}
            className="w-full h-[300px] md:h-[440px] object-cover"
          />
        </div>
        <div
          data-testid="gallery-item-3"
          className="md:col-span-7 wr-zoom rounded-sm reveal"
          style={{ transitionDelay: "140ms" }}
        >
          <img
            src={media.gallery[3].url}
            alt={media.gallery[3].alt}
            className="w-full h-[300px] md:h-[440px] object-cover"
          />
        </div>
      </div>
    </div>
  </section>
);

/* === LOCATION / CONTACT === */
const Location = ({ t }) => (
  <section
    id="contact"
    data-testid="contact-section"
    className="bg-[#24322B] text-white py-24 md:py-32 relative overflow-hidden"
  >
    <div className="absolute inset-0 wr-grain pointer-events-none" />
    <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20 items-center relative">
      <div>
        <span className="wr-overline reveal" style={{ color: "#C2B280" }}>
          — {t.location.label}
        </span>
        <h2
          data-testid="contact-headline"
          className="reveal font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-light mt-6 text-white"
          style={{ transitionDelay: "100ms" }}
        >
          {t.location.headline}
        </h2>

        <div className="mt-12 space-y-8 max-w-md">
          <div className="reveal" style={{ transitionDelay: "160ms" }}>
            <div className="text-[11px] tracking-[0.22em] uppercase text-white/55">
              {t.location.addressLabel}
            </div>
            <div className="mt-2 flex items-start gap-3 text-white/95">
              <MapPin size={18} strokeWidth={1.4} className="mt-0.5 text-[#C2B280]" />
              <span data-testid="contact-address" className="font-serif text-2xl">
                {t.location.address}
              </span>
            </div>
          </div>

          <div className="reveal" style={{ transitionDelay: "220ms" }}>
            <div className="text-[11px] tracking-[0.22em] uppercase text-white/55">
              {t.location.phoneLabel}
            </div>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="contact-phone-link"
              className="mt-2 flex items-center gap-3 text-white hover:text-[#C2B280] transition-colors duration-300"
            >
              <Phone size={18} strokeWidth={1.4} className="text-[#C2B280]" />
              <span className="font-serif text-2xl">{PHONE}</span>
            </a>
          </div>

          <div className="reveal pt-4" style={{ transitionDelay: "280ms" }}>
            <a
              href={`tel:${PHONE_TEL}`}
              data-testid="call-to-reserve-contact-button"
              className="group inline-flex items-center gap-3 bg-[#C2B280] hover:bg-[#b3a26f] text-[#24322B] px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold transition-all duration-300 active:scale-95"
            >
              <Phone size={16} strokeWidth={1.8} />
              <span>{t.location.cta}</span>
              <ArrowUpRight
                size={16}
                strokeWidth={1.8}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </a>
          </div>
        </div>
      </div>

      <div
        className="reveal rounded-sm overflow-hidden border border-white/10 shadow-2xl"
        style={{ transitionDelay: "120ms" }}
      >
        <iframe
          data-testid="contact-map"
          title={t.location.mapTitle}
          src={media.mapSrc}
          width="100%"
          height="460"
          style={{ border: 0, display: "block" }}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
    </div>
  </section>
);

/* === FOOTER === */
const Footer = ({ t }) => (
  <footer
    data-testid="site-footer"
    className="bg-[#FBF9F6] border-t border-[#E5E0D8] py-12"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="font-serif text-xl text-[#1A1A1A]">
        White Residence Arad<span className="text-[#C2B280]">.</span>
      </div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A] text-center">
        {t.footer.tagline}
      </div>
      <div className="text-[11px] tracking-[0.22em] uppercase text-[#4A4A4A]">
        © {new Date().getFullYear()} — {t.footer.rights}
      </div>
    </div>
  </footer>
);

/* === STICKY CALL CTA === */
const StickyCallCta = ({ t }) => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 600);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <a
      href={`tel:${PHONE_TEL}`}
      data-testid="call-to-reserve-sticky-button"
      aria-label={t.hero.cta}
      className={`fixed z-50 bottom-20 right-6 md:bottom-24 md:right-8 inline-flex items-center gap-3 bg-[#2B4A3B] hover:bg-[#1D3227] text-white px-5 py-4 md:px-6 md:py-4 rounded-full shadow-2xl wr-pulse transition-all duration-500 active:scale-95 ${
        show
          ? "opacity-100 translate-y-0 pointer-events-auto"
          : "opacity-0 translate-y-3 pointer-events-none"
      }`}
    >
      <Phone size={18} strokeWidth={1.8} />
      <span className="hidden md:inline text-[12px] tracking-[0.22em] uppercase font-medium">
        {t.hero.cta}
      </span>
    </a>
  );
};

/* === APP === */
function App() {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("wr-lang") || "ro";
    }
    return "ro";
  });
  const t = translations[lang];
  const isFirst = useRef(true);
  useReveal();

  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("wr-lang", lang);
      document.documentElement.lang = lang;
    }
    // Re-trigger reveals on language switch except initial mount
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    const els = document.querySelectorAll(".reveal");
    els.forEach((el) => el.classList.add("is-visible"));
  }, [lang]);

  return (
    <div className="App bg-[#FBF9F6] text-[#1A1A1A]">
      <Header t={t} lang={lang} setLang={setLang} />
      <Hero t={t} />
      <About t={t} />
      <Amenities t={t} />
      <Gallery t={t} />
      <Location t={t} />
      <Footer t={t} />
      <StickyCallCta t={t} />
    </div>
  );
}

export default App;
